import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import MagicVerify from "@/pages/MagicVerify";
import HouseholdPicker from "@/pages/HouseholdPicker";
import MiloApp from "@/pages/MiloApp";
import AdminPage from "@/pages/AdminPage";
import Pricing from "@/pages/Pricing";
import RecipesPage from "@/pages/RecipesPage";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { loadActiveHousehold } from "@/lib/api";

function Protected({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">
                Loading…
            </div>
        );
    }
    if (!user) {
        // preserve any ?code=... so post-login we can deep-join,
        // and redirect new invitees straight to /signup so they don't bounce through /login
        const code = new URLSearchParams(location.search).get("code");
        if (code) {
            sessionStorage.setItem("milo_pending_code", code);
            return <Navigate to={`/signup?code=${code}`} replace />;
        }
        return <Navigate to="/login" replace />;
    }
    return children;
}

function AppEntry() {
    const active = loadActiveHousehold();
    if (!active?.household_id) return <Navigate to="/picker" replace />;
    return <MiloApp />;
}

function RootRedirect() {
    const location = useLocation();
    return <Navigate to={`/app${location.search}`} replace />;
}

function RootRouter() {
    const location = useLocation();
    // CRITICAL: detect auth callback hash early
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/magic" element={<MagicVerify />} />
            <Route
                path="/picker"
                element={
                    <Protected>
                        <HouseholdPicker />
                    </Protected>
                }
            />
            <Route
                path="/app"
                element={
                    <Protected>
                        <AppEntry />
                    </Protected>
                }
            />
            <Route
                path="/admin"
                element={
                    <Protected>
                        <AdminPage />
                    </Protected>
                }
            />
            <Route path="/pricing" element={<Pricing />} />
            <Route
                path="/recipes"
                element={
                    <Protected>
                        <RecipesPage />
                    </Protected>
                }
            />
            <Route
                path="/inventory"
                element={<Navigate to="/app?tab=inventory" replace />}
            />
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App grain-overlay">
            <AuthProvider>
                <I18nProvider>
                    <BrowserRouter>
                        <RootRouter />
                    </BrowserRouter>
                </I18nProvider>
            </AuthProvider>
            <Toaster position="top-center" />
        </div>
    );
}

export default App;
